import { Metadata } from 'nice-grpc-web';
import { v4 as uuidv4 } from 'uuid';
// import { NodeHttpTransport } from '@improbable-eng/grpc-web';

import { LoginError, LoginStatus, User } from '@/state/reducers/authSlice';
import { MarketItem } from '@/state/reducers/celerMarketSlice';

import { ResetUserPasswordToken } from '@/compiled_proto/com/celertech/baseserver/api/user/DownstreamAuthenticationUserProto';
import {
    LoginResponse,
    LoginResponseType
} from '@/compiled_proto/com/celertech/baseserver/communication/login/DownstreamLoginProto';
import { GrpcLogoutMessage } from '@/compiled_proto/com/celertech/baseserver/communication/login/LogoutProto';
import { PiggyBankServiceClient } from '@/compiled_proto/com/celertech/piggybank/api/PiggyBankServiceProto';
import { SubLedgerSnapshotDownstreamEvent } from '@/compiled_proto/com/celertech/piggybank/api/subledger/DownstreamSubLedgerProto';
import {
    loginServiceClient,
    logoutServiceClient,
    piggyBankServiceClient,
    preferenceClient,
    resetPasswordClient,
    userAccountServiceClient,
    userLimitServiceClient
} from '@/helpers/environmentHelper';
import { logToServer } from './LogService';
import { extractBalanceUpdate } from './utils/BalanceUpdateParser';

const AUTHORITY = window.config.integration.celertech.authority;

// const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
// const channel = createChannel(CHANNEL_URL, isBrowser ? undefined : NodeHttpTransport());

export const getAuthMetadata = (credentials) => ({
    metadata: Metadata({
        'authorization-token': credentials.authToken
    })
});

export async function login(
    username: string,
    password: string,
    twoFactorToken?: string
): Promise<Omit<LoginResponse, 'accounts'>> {
    const resp = await loginServiceClient.unary.login(
        { username, password, twoFactorToken },
        {
            metadata: Metadata({
                authority: AUTHORITY
            })
        }
    );
    if (resp.loginResponseType == LoginResponseType.LOGGED_IN) {
        logToServer('info', `User [${username}] has logged in.`);
        return resp;
    } else if (resp.loginResponseType == LoginResponseType.BAD_CREDENTIALS) {
        logToServer('info', `User [${username}] has entered invalid credentials or Celer system is acting up.`);
        throw new LoginError(LoginStatus.BAD_CREDENTIALS);
    } else if (resp.loginResponseType == LoginResponseType.QR_TOKEN_CODE_REQUIRED) {
        return resp;
    } else {
        logToServer('info', `User [${username}] encountered an unexpected error while trying to login.`);
        throw new LoginError(LoginStatus.UNEXPECTED);
    }
}

export async function logout(credentials: User): Promise<GrpcLogoutMessage> {
    const resp = await logoutServiceClient.unary.logout({}, getAuthMetadata(credentials));
    return resp;
}

export async function sendResetPasswordEmail(username: string): Promise<ResetUserPasswordToken> {
    const resp = await resetPasswordClient.unary.generatePasswordToken({ username, sendUserNotification: true }, {});
    return resp;
}

export async function resetUserPassword(credentials: User, currentPassword: string, newPassword: string) {
    const { username, clientRequestId } = credentials;

    const { resetUserPasswordToken: resetPasswordToken } = await resetPasswordClient.unary.generatePasswordToken({
        clientRequestId,
        username,
        sendUserNotification: false
    });

    const resp = await resetPasswordClient.unary.resetUsersPassword({
        clientRequestId,
        username,
        currentPassword,
        newPassword,
        resetPasswordToken
    });
    return resp;
}

export async function resetUserPasswordWithToken(resetPasswordToken: string, username: string, newPassword: string) {
    const resp = await resetPasswordClient.unary.resetUsersPassword({
        clientRequestId: uuidv4(),
        username,
        currentPassword: undefined,
        newPassword,
        resetPasswordToken
    });
    return resp;
}

export async function getUserProfile(credentials: User): Promise<string> {
    const resp = await preferenceClient.unary.findProfilesByUsernameAndType(
        { type: 'USER' },
        getAuthMetadata(credentials)
    );

    const profileId = resp.profile[0].id;
    return profileId;
}

type UserAccountsReturn = {
    code: string;
    name: string;
}[];

export async function getUserAccounts(credentials: User): Promise<UserAccountsReturn> {
    const resp = await userAccountServiceClient.unary.findAllAccountsByUser(
        { username: credentials.username },
        { metadata: Metadata({ 'authorization-token': credentials.authToken }) }
    );

    return resp.userAccount
        .map((ua) => ({ code: ua.account?.code || '', name: ua.account?.name || '' }))
        .filter((ua) => ua.code) as UserAccountsReturn;
}

export async function getUserMarkets(credentials): Promise<MarketItem[]> {
    const resp = await userLimitServiceClient.unary.findUserLimitsByUsername(
        {
            clientRequestId: uuidv4(),
            username: credentials.username
        },
        getAuthMetadata(credentials)
    );

    const userMarkets = resp.limit
        .filter((x) => x.enabled)
        .filter((x) => x.securityCode.indexOf('/') !== -1)
        .map((x) => {
            return {
                securityCode: x.securityCode,
                exchangeCode: x.exchangeCode
            } as any;
        });

    // Sort alphabetically by securityCode
    userMarkets.sort((a, b) => (a.securityCode > b.securityCode ? 1 : -1));

    return userMarkets;
}

export async function getUserBalances(piggybankClient: PiggyBankServiceClient, credentials: User, currentAccount) {
    // const findAllCurrencyPositionsSubscription = piggybankClient.findAllCurrencyPositionsByAccount(
    //     { account: currentAccount || credentials.accounts[0].code },
    //     {
    //         metadata: Metadata({
    //             deadline: new Date().setSeconds(new Date().getSeconds() + 86400).toString(),
    //             'authorization-token': credentials.authToken
    //         })
    //     }
    // );
    // const items = await asyncList(findAllCurrencyPositionsSubscription, (event: PiggyBankUpdateEvent) => ({
    //     currency: event.subLedgerSnapshotDownstreamEvent?.currency,
    //     netPosition: event.subLedgerSnapshotDownstreamEvent?.netPosition,
    //     notionalCurrency: event.subLedgerSnapshotDownstreamEvent?.notionalCurrency,
    //     notionalNetPosition: event.subLedgerSnapshotDownstreamEvent?.notionalNetPosition,
    //     settlementDate: event.subLedgerSnapshotDownstreamEvent?.settlementDate
    // }));
    // return items;
}

export async function fetchLatestBalance(credentials: User, currentAccount?: string) {
    const account = currentAccount || credentials.accounts[0].code;
    const findAllCurrencyPositionsSubscription = piggyBankServiceClient.unary.findAllCurrencyPositionsByAccount(
        { account: [account] },
        {
            metadata: Metadata({
                deadline: new Date().setSeconds(new Date().getSeconds() + 86400).toString(),
                'authorization-token': credentials.authToken
            })
        }
    );
    const balanceEvents = await findAllCurrencyPositionsSubscription;
    const balanceItems = balanceEvents.piggyBankUpdateEvent
        .filter((ev) => ev.subLedgerSnapshotDownstreamEvent)
        .map((ev) => extractBalanceUpdate(ev.subLedgerSnapshotDownstreamEvent as SubLedgerSnapshotDownstreamEvent));
    return balanceItems;
}
